'use client'

import { Button } from '@/core/components/form/button/button'
import { useLatest } from '@/core/hooks/use-latest'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import Link from 'next/link'
import {
  LuArrowLeft,
  LuChevronDown,
  LuChevronUp,
  LuHeart,
} from 'react-icons/lu'

import { animateScroll, prefersReducedMotion } from '@/core/lib/motion'
import { toast } from '@/core/lib/toast'
import { useMediaQuery } from '@/core/hooks/use-media-query'
import { cn } from '@/core/utils/common'

import CommentsPanel, {
  COMMENTS_SHEET_CLASS,
  CommentsSheet,
} from '@/features/veluvit/components/comments/comments-panel'
import ReelSlide from '@/features/veluvit/components/explore/reel-slide'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { FEED_TABS, feedList } from '@/features/veluvit/lib/explore'
import { EASE_OUT, gsap } from '@/features/veluvit/lib/gsap'
import { back } from '@/features/veluvit/lib/navigation'
import { inLane } from '@/features/veluvit/lib/rules'
import { setFavorite } from '@/features/veluvit/services/catalog.actions'
import { openSheet } from '@/features/veluvit/stores/overlay.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { FeedTab } from '@/features/veluvit/lib/explore'

/** 한 장 넘긴 뒤 다음 넘김을 받지 않는 시간(부드러운 스크롤이 끝날 때까지) */
const PAGE_LOCK_MS = 450
/** 휠 이벤트 사이가 이보다 짧으면 같은 한 번의 굴림(관성 포함)으로 본다 */
const WHEEL_GAP_MS = 180

/** 처음 한 번만 보여 주는 넘기기 안내(온보딩) — 닫거나 한 장이라도 넘기면 다시 안 뜬다 */
const HINT_KEY = 'veluvit:explore-swiped'

const subscribeNever = () => () => {}
const readHintSeenOnServer = () => true
const readHintSeen = () => {
  try {
    return localStorage.getItem(HINT_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * 탐색 — 9:16 세로 스냅 릴스. 위 탭(추천·인기·최신·좋아요)으로 같은 작품을 다른 순서로 넘겨 본다.
 * 목록은 백엔드가 발행한 작품(bootstrap.works), 좋아요는 계정 데이터(favorites), 정렬 기준은 lib/explore.
 * 지금 보는 장·탭은 주소(?tab=·?c=)에 남겨 상세에 갔다 돌아와도, 공유 링크로 들어와도 그 장에서 시작한다.
 */
export default function ExploreScreen({
  initialTab = 'pick',
  initialWorkId,
}: {
  initialTab?: FeedTab
  initialWorkId?: string
}) {
  const desktop = useMediaQuery('(min-width: 1001px)')
  const { catalog } = useCatalogMedia()
  const storedFavorites = useSessionStore((state) => state.data?.favorites)
  const pref = useUiStore((s) => s.pref)
  const [tab, setTab] = useState<FeedTab>(initialTab)
  /** 좋아요 탭에서 하트를 꺼도 그 장이 바로 사라지지 않게 — 탭에 들어온 순간의 목록 */
  const [likedSnapshot, setLikedSnapshot] = useState<string[] | null>(null)
  /** 누르자마자 보이는 좋아요 — 서버 응답이 오면 지운다(실패하면 되돌린다) */
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({})
  // 서버와 첫 hydration에서는 안내를 숨겼다가, 저장값을 읽어 처음인 사람에게만 보인다
  const storedHintSeen = useSyncExternalStore(
    subscribeNever,
    readHintSeen,
    readHintSeenOnServer
  )
  const [hintDismissed, setHintDismissed] = useState(false)
  const hintSeen = storedHintSeen || hintDismissed
  /** PC 댓글 패널 — 열려 있으면 지금 걸린 장의 댓글을 보인다 */
  const [commentsOpen, setCommentsOpen] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const tabRefs = useRef(new Map<FeedTab, HTMLButtonElement>())
  const [bar, setBar] = useState<{ left: number; width: number } | null>(null)
  const landing = useRef<string | null>(initialWorkId ?? null)

  let pick = inLane(catalog, catalog.order, pref)
  if (!pick.length) pick = catalog.order.filter((id) => !!catalog.chars[id])
  const favorites = storedFavorites || []
  const liveLiked = feedList(catalog, pick, favorites, 'liked')
  const ids =
    tab === 'liked'
      ? likedSnapshot || liveLiked
      : feedList(catalog, pick, favorites, tab)
  const [active, setActive] = useState(() =>
    initialWorkId ? Math.max(0, ids.indexOf(initialWorkId)) : 0
  )
  const likedSet = new Set(favorites)
  const isLiked = (id: string) => optimistic[id] ?? likedSet.has(id)
  const current = ids[Math.min(active, ids.length - 1)]
  const idsKey = ids.join('|')

  // 시작 장으로 곧장 이동 (목록이 준비된 첫 순간 한 번)
  useLayoutEffect(() => {
    const el = scroller.current
    const target = landing.current
    if (!el || !target || !ids.length) return
    landing.current = null
    const index = ids.indexOf(target)
    if (index > 0) el.scrollTo({ top: index * el.clientHeight })
  }, [ids])

  // 화면에 걸린 장 추적 — 절반 넘게 보이는 장이 지금 장
  useEffect(() => {
    const root = scroller.current
    if (!root) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          const index = Number((e.target as HTMLElement).dataset.index)
          if (Number.isFinite(index)) setActive(index)
        }
      },
      { root, threshold: 0.6 }
    )
    root.querySelectorAll('[data-index]').forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [idsKey])

  // 지금 장·탭을 주소에 — 기록을 쌓지 않고 바꿔 둔다
  useEffect(() => {
    if (!current) return
    const params = new URLSearchParams(window.location.search)
    if (tab === 'pick') params.delete('tab')
    else params.set('tab', tab)
    params.set('c', current)
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}`
    )
  }, [tab, current])

  const dismissHint = () => setHintDismissed(true)
  // 안내를 닫기 전에 넘겨 버려도(키보드·버튼) 본 것으로 친다
  if (!hintSeen && active >= 1) setHintDismissed(true)
  useEffect(() => {
    if (!hintDismissed) return
    try {
      localStorage.setItem(HINT_KEY, '1')
    } catch {
      // 저장소를 못 쓰면 이번 방문 동안만 숨긴다
    }
  }, [hintDismissed])

  // 탭 밑줄 — 한 줄이 고른 탭 아래로 미끄러진다
  useLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current.get(tab)
      if (el) setBar({ left: el.offsetLeft, width: el.offsetWidth })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [tab])

  /** 한 장씩 넘기기 — PC 버튼·키보드·휠. 넘기는 애니메이션 동안(PAGE_LOCK_MS)은 다음 넘김을 받지 않는다 */
  const lockUntil = useRef(0)
  const stopScroll = useRef<(() => void) | null>(null)
  const page = (dir: 1 | -1) => {
    const el = scroller.current
    const now = performance.now()
    if (!el || now < lockUntil.current) return
    lockUntil.current = now + PAGE_LOCK_MS
    stopScroll.current?.()
    stopScroll.current = animateScroll(
      el,
      (Math.round(el.scrollTop / el.clientHeight) + dir) * el.clientHeight
    )
  }
  useEffect(() => () => stopScroll.current?.(), [])

  // 휠 — 확 굴려도 한 장만(2026-10-08 "너무 내려가 버림"). 브라우저 기본 스크롤을 막고 한 번의 굴림(사이가 WHEEL_GAP_MS 안으로 이어지는 휠 이벤트 묶음 —
  // 트랙패드 관성 꼬리 포함)을 한 장 넘김으로 바꾼다. 다음 장은 손을 멈췄다 다시 굴려야 넘어간다(디바운스)
  const pageRef = useLatest(page)
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    let last = 0
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return // 핀치 확대는 그대로
      e.preventDefault()
      const now = performance.now()
      const idle = now - last
      last = now
      if (idle < WHEEL_GAP_MS || Math.abs(e.deltaY) < 2) return
      pageRef.current(e.deltaY > 0 ? 1 : -1)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [pageRef])

  const onLike = (id: string, favorite: boolean) => {
    setOptimistic((prev) => ({ ...prev, [id]: favorite }))
    setFavorite(id, favorite)
      .catch((error: unknown) =>
        toast.show(
          error instanceof Error && error.message
            ? error.message
            : '연결을 확인해 주세요.',
          { duration: 3200 }
        )
      )
      .finally(() =>
        setOptimistic((prev) => {
          const next = { ...prev }
          delete next[id]
          return next
        })
      )
  }

  /**
   * 말풍선(댓글) — 유튜브 쇼츠처럼 PC 는 카드 오른쪽에 패널을 붙이고(넘기면 패널도 그 장의 댓글로 바뀐다), 모바일은 바텀시트.
   * PC 에서 같은 말풍선을 다시 누르면 닫는다
   */
  const openComments = (id: string) => {
    if (window.matchMedia('(min-width: 1001px)').matches) {
      setCommentsOpen((v) => !v)
      return
    }
    openSheet(<CommentsSheet workId={id} />, {
      label: '댓글',
      tall: true,
      className: COMMENTS_SHEET_CLASS,
    })
  }

  // PC 키보드 — ↑↓(J/K) 넘기기, L 좋아요, Esc 댓글 패널 닫기. 입력칸·레이어 위에서는 가만히 둔다
  const keyState = useLatest({ current, onLike, isLiked, commentsOpen })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return
      const target = e.target as HTMLElement | null
      if (
        target?.closest(
          'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]'
        )
      )
        return
      const key = e.key.toLowerCase()
      if (key === 'arrowdown' || key === 'j') page(1)
      else if (key === 'arrowup' || key === 'k') page(-1)
      else if (key === 'l') {
        const { current: id, onLike: like, isLiked: liked } = keyState.current
        if (id) like(id, !liked(id))
      } else if (key === 'escape' && keyState.current.commentsOpen)
        setCommentsOpen(false)
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [keyState])

  const pickTab = (next: FeedTab) => {
    if (next === tab) {
      // 고른 탭을 다시 누르면 맨 위로 (릴스·쇼츠와 같다)
      stopScroll.current?.()
      if (scroller.current)
        stopScroll.current = animateScroll(scroller.current, 0)
      return
    }
    stopScroll.current?.()
    setTab(next)
    setActive(0)
    setLikedSnapshot(next === 'liked' ? liveLiked : null)
    scroller.current?.scrollTo({ top: 0 })
    if (list.current && !prefersReducedMotion()) {
      gsap.fromTo(
        list.current,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.28, ease: EASE_OUT }
      )
    }
  }

  return (
    <>
      {/* 위 막대 — 뒤로 · 탭. 모바일은 사진 위에 떠 있어 글씨에 그림자를 주고, PC 는 카드 위 한 줄(52px)로 따로 선다(쇼츠의 검색 줄 자리) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[5] pt-[max(12px,var(--safe-top))] over10:pt-1">
        <div className="relative flex h-11 items-center justify-center over10:h-12">
          <Button
            type="button"
            aria-label="뒤로"
            onClick={back}
            className="pointer-events-auto absolute left-2 grid size-10 cursor-pointer place-items-center rounded-full text-white transition-colors duration-150 [filter:drop-shadow(0_1px_4px_rgb(0_0_0/0.5))] hover:bg-foreground/10 focus-visible:bg-foreground/10 over10:hidden"
            variant="bare"
          >
            <LuArrowLeft aria-hidden="true" className="size-6" />
          </Button>
          <div
            role="tablist"
            aria-label="탐색 목록"
            className="pointer-events-auto relative flex items-center gap-5 max-[359px]:gap-3.5"
          >
            {FEED_TABS.map((t) => (
              <Button
                key={t.key}
                ref={(el) => {
                  if (el) tabRefs.current.set(t.key, el)
                  else tabRefs.current.delete(t.key)
                }}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => pickTab(t.key)}
                className={cn(
                  'cursor-pointer py-2 text-c16 font-bold transition-colors duration-200 [text-shadow:0_1px_6px_rgb(0_0_0/0.55)] max-[359px]:text-c15',
                  tab === t.key
                    ? 'text-white'
                    : 'text-white/55 hover:text-white/85'
                )}
                variant="bare"
              >
                {t.label}
              </Button>
            ))}
            {/* 밑줄 하나가 탭 사이를 미끄러진다 — 글자 폭만큼 */}
            {bar && (
              <span
                aria-hidden="true"
                className="absolute bottom-0.5 h-0.75 rounded-full bg-white shadow-[0_1px_4px_rgb(0_0_0/0.4)] transition-[left,width] duration-300 ease-smooth"
                style={{ left: bar.left, width: bar.width }}
              />
            )}
          </div>
        </div>
      </div>

      {/* PC — 화면 오른쪽 끝 가운데: 이전/다음과 몇 번째 장인지(쇼츠와 같은 자리). 카드 옆은 액션바가 쓴다 */}
      {ids.length > 1 && !commentsOpen && (
        <div className="absolute top-1/2 right-8 z-[6] hidden -translate-y-1/2 flex-col items-center gap-2.5 over10:flex">
          <Button
            type="button"
            aria-label="이전 캐릭터"
            aria-keyshortcuts="ArrowUp"
            title="이전 (↑)"
            disabled={active <= 0}
            onClick={() => page(-1)}
            className="grid size-11 cursor-pointer place-items-center rounded-full text-white glass transition-[background-color,opacity] duration-150 hover:bg-foreground/[0.16] focus-visible:bg-foreground/[0.16] disabled:cursor-default disabled:opacity-30 disabled:hover:bg-[rgb(44_44_46/0.42)]"
            variant="bare"
          >
            <LuChevronUp aria-hidden="true" className="size-5" />
          </Button>
          <span className="text-c11 text-t3 tabular-nums" aria-live="polite">
            {Math.min(active + 1, ids.length)} / {ids.length}
          </span>
          <Button
            type="button"
            aria-label="다음 캐릭터"
            aria-keyshortcuts="ArrowDown"
            title="다음 (↓)"
            disabled={active >= ids.length - 1}
            onClick={() => page(1)}
            className="grid size-11 cursor-pointer place-items-center rounded-full text-white glass transition-[background-color,opacity] duration-150 hover:bg-foreground/[0.16] focus-visible:bg-foreground/[0.16] disabled:cursor-default disabled:opacity-30 disabled:hover:bg-[rgb(44_44_46/0.42)]"
            variant="bare"
          >
            <LuChevronDown aria-hidden="true" className="size-5" />
          </Button>
        </div>
      )}

      <div
        ref={scroller}
        // PC — 예전 430px 고정 기둥(양옆 선) 대신 화면 폭 전체가 넘기는 영역, 장마다 위아래 여백을 둔 9:16 카드(reel-slide). 탭 줄(52px) 아래에서 시작
        // 댓글 패널(420 + 여백)이 열리면 넘기는 영역 오른쪽을 비워 카드가 왼쪽으로 비켜선다(쇼츠)
        className={cn(
          'absolute inset-0 snap-y snap-mandatory overflow-y-auto overscroll-contain scrollbar-none over10:top-13 over10:transition-[right] over10:duration-300 over10:ease-smooth',
          commentsOpen && 'over10:right-[456px]'
        )}
      >
        <div ref={list} className="h-full">
          {!ids.length && (
            <div className="grid h-full place-items-center bg-black px-8 over10:bg-transparent">
              <EmptyState
                icon={<LuHeart />}
                title={
                  tab === 'liked'
                    ? '좋아요한 작품이 없어요'
                    : '아직 볼 수 있는 작품이 없어요'
                }
                description={
                  tab === 'liked'
                    ? '마음에 드는 작품에 하트를 눌러 모아 보세요.'
                    : '공개된 작품이 올라오면 이곳에서 만날 수 있어요.'
                }
                action={
                  tab === 'liked' ? (
                    <Button
                      type="button"
                      onClick={() => pickTab('pick')}
                      className="cursor-pointer text-point underline underline-offset-4"
                      variant="bare"
                    >
                      추천 보러 가기
                    </Button>
                  ) : (
                    <Link
                      href="/"
                      className="text-point underline underline-offset-4"
                    >
                      홈으로 가기
                    </Link>
                  )
                }
              />
            </div>
          )}
          {ids.map((id, i) => (
            <div
              key={`${tab}-${id}`}
              data-index={i}
              className="h-full snap-start snap-always"
            >
              <ReelSlide
                id={id}
                index={i}
                active={i === active}
                near={Math.abs(i - active) <= 1}
                liked={isLiked(id)}
                onLike={onLike}
                onComments={openComments}
                commentsOpen={commentsOpen && id === current}
              />
            </div>
          ))}
        </div>
      </div>

      {/* PC 댓글 패널 — 넘기는 영역 오른쪽, 탭 줄 아래부터 카드 아래 여백까지. 모바일은 말풍선이 바텀시트를 연다 */}
      {desktop && commentsOpen && current && (
        <CommentsPanel
          workId={current}
          onClose={() => setCommentsOpen(false)}
          className="absolute top-15 right-6 bottom-6 z-[6] hidden w-[420px] animate-fade-in over10:flex"
        />
      )}

      {/* 첫 방문 온보딩 — 릴스 기둥을 한 번 덮고, 누르거나 밀면 닫힌다. 이후엔 다시 뜨지 않는다 */}
      {!hintSeen && ids.length > 1 && (
        <div
          role="dialog"
          aria-label="탐색 사용법"
          onClick={dismissHint}
          onWheel={dismissHint}
          onTouchMove={dismissHint}
          className="absolute inset-0 z-[7] grid cursor-pointer place-items-center bg-black/60 backdrop-blur-xl animate-[fade-in_0.3s_ease-out]"
        >
          <div className="flex flex-col items-center px-8 text-center text-white">
            {/* 손가락 대신 점 하나가 트랙을 따라 위로 쓸려 올라간다 */}
            <span
              aria-hidden="true"
              className="relative h-18 w-10 rounded-full border-2 border-white/70"
            >
              <span className="absolute left-1/2 size-3 -translate-x-1/2 rounded-full bg-white animate-[swipe-dot_1.6s_ease-in-out_infinite] motion-reduce:top-2 motion-reduce:animate-none" />
            </span>
            <p className="mt-5 text-c17 font-bold">
              <span className="over10:hidden">위로 밀어 다음 캐릭터</span>
              <span className="hidden over10:inline">
                휠이나 ↑ ↓ 키로 다음 캐릭터
              </span>
            </p>
            <p className="mt-1.5 text-c13 leading-[1.6] text-white/70">
              사진을 두 번 톡 하면 좋아요
            </p>
            <Button
              type="button"
              onClick={dismissHint}
              className="mt-6 h-10 cursor-pointer rounded-full bg-white px-6 text-c14 font-bold text-black transition-colors hover:bg-t2 focus-visible:bg-t2"
              variant="bare"
            >
              시작하기
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
